import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchExampleQuestions } from '../services/exampleQuestionsService';
import { resolveLocale } from '../localization/translations';
import type { ExampleQuestionCategory } from '../types/exampleQuestions';
import {
    buildExampleQuestionsCacheKey,
    normalizeExampleEndpointForCache,
    normalizeExampleQuestionsCacheTtlMs,
    readExampleQuestionsCache,
    writeExampleQuestionsCache
} from './useExampleQuestions/exampleQuestionsCache';

export type UseExampleQuestionsOptions = {
    endpoint?: string | null;
    headers?: Record<string, string> | null;
    language?: string | null;
    cacheTtlMs?: number;
};

export type UseExampleQuestionsResult = {
    categories: ExampleQuestionCategory[];
    isLoading: boolean;
    error?: string;
    refetch: () => void;
};

const defaultResult: ExampleQuestionCategory[] = [];
const shortRetryDelayMs = 10_000;
const longRetryDelayMs = 120_000;
const shortRetryLimit = 3;
const maxRetryWindowMs = 300_000;

export function useExampleQuestions(options: UseExampleQuestionsOptions): UseExampleQuestionsResult {
    const { endpoint, headers, language, cacheTtlMs } = options;
    const normalizedEndpoint = useMemo(() => normalizeExampleEndpointForCache(endpoint), [endpoint]);
    const normalizedLanguage = useMemo(() => resolveLocale(language), [language]);
    const normalizedCacheTtlMs = useMemo(() => normalizeExampleQuestionsCacheTtlMs(cacheTtlMs), [cacheTtlMs]);
    const cacheKey = useMemo(
        () => buildExampleQuestionsCacheKey(normalizedEndpoint, normalizedLanguage),
        [normalizedEndpoint, normalizedLanguage]
    );
    const cacheState = useMemo(
        () => readExampleQuestionsCache(cacheKey, normalizedCacheTtlMs),
        [cacheKey, normalizedCacheTtlMs]
    );
    const hasSubscriptionKey = useMemo(() => containsSubscriptionKeyHeader(headers), [headers]);

    const [categories, setCategories] = useState<ExampleQuestionCategory[]>(() =>
        cacheState.status === 'hit' ? cacheState.categories : defaultResult
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [refreshCount, setRefreshCount] = useState(0);

    const refetch = useCallback(() => {
        setRefreshCount((value) => value + 1);
    }, []);

    const retryStateRef = useRef<{ startedAt: number | null; attempts: number }>({
        startedAt: null,
        attempts: 0
    });

    const resetRetryState = useCallback(() => {
        retryStateRef.current.startedAt = null;
        retryStateRef.current.attempts = 0;
    }, []);

    useEffect(() => {
        if (!cacheKey) {
            setCategories(defaultResult);
            setError(undefined);
            return;
        }

        setCategories(cacheState.status === 'hit' ? cacheState.categories : defaultResult);
        setError(undefined);
    }, [cacheKey, cacheState]);

    useEffect(() => {
        if (!normalizedEndpoint) {
            setCategories(defaultResult);
            setError(undefined);
            setIsLoading(false);
            resetRetryState();
            return;
        }

        if (!hasSubscriptionKey) {
            setIsLoading(false);
            setError(undefined);
            resetRetryState();
            return;
        }

        if (refreshCount === 0 && cacheState.status === 'hit') {
            setIsLoading(false);
            setError(undefined);
            resetRetryState();
            return;
        }

        const controller = new AbortController();
        setIsLoading(true);
        setError(undefined);

        fetchExampleQuestions({
            endpoint: normalizedEndpoint,
            headers: headers ?? undefined,
            signal: controller.signal,
            language: normalizedLanguage
        })
            .then((response) => {
                if (!controller.signal.aborted) {
                    const nextCategories = response.categories ?? defaultResult;
                    setCategories(nextCategories);
                    writeExampleQuestionsCache(cacheKey, nextCategories);
                    resetRetryState();
                }
            })
            .catch((err) => {
                if (!controller.signal.aborted) {
                    const message = err instanceof Error ? err.message : 'Failed to load example prompts.';
                    setError(message);
                    setCategories((current) => {
                        if (current.length > 0) {
                            return current;
                        }

                        const cached = readExampleQuestionsCache(cacheKey, normalizedCacheTtlMs);
                        return cached.status === 'hit' ? cached.categories : defaultResult;
                    });
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [
        normalizedEndpoint,
        headers,
        normalizedLanguage,
        refreshCount,
        cacheKey,
        cacheState.status,
        hasSubscriptionKey,
        normalizedCacheTtlMs,
        resetRetryState
    ]);

    useEffect(() => {
        if (!normalizedEndpoint || !hasSubscriptionKey || !error || isLoading || categories.length > 0) {
            if (!error || categories.length > 0) {
                resetRetryState();
            }
            return;
        }

        const now = Date.now();
        const retryState = retryStateRef.current;

        if (!retryState.startedAt) {
            retryState.startedAt = now;
        }

        const elapsed = now - retryState.startedAt;
        const delay = retryState.attempts < shortRetryLimit ? shortRetryDelayMs : longRetryDelayMs;

        if (elapsed >= maxRetryWindowMs || elapsed + delay > maxRetryWindowMs) {
            return;
        }

        retryState.attempts += 1;

        const retryTimeout = window.setTimeout(() => {
            refetch();
        }, delay);

        return () => clearTimeout(retryTimeout);
    }, [normalizedEndpoint, hasSubscriptionKey, error, isLoading, categories.length, refetch, resetRetryState]);

    return {
        categories,
        isLoading,
        error,
        refetch
    };
}

function containsSubscriptionKeyHeader(headers?: Record<string, string> | null): boolean {
    if (!headers) {
        return false;
    }

    return Object.entries(headers).some(
        ([key, value]) => key.trim().toLowerCase() === 'ocp-apim-subscription-key' && value.trim().length > 0
    );
}
